'use client';

import { useCallback } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useNotificationBanner } from '@enter/business-components/components/notification-banner';
import type {
    NotificationBannerCarouselItem,
    NotificationBannerProps,
} from '@enter/business-components/components/notification-banner';
import { useEnterApi } from '@enter/api';
import { useEnterHomeLogin } from '@/contexts/auth/use-enter-home-login';
import {
    resolveEnterHomeNotificationBannerCloseId,
    resolveEnterHomeNotificationBannerResponse,
} from '@/features/notifications/model/banner';

/**
 * enter-home 共用 Notification Banner Hook
 *
 * 封裝 useNotificationBanner、handleBannerCtaClick、noopMarkAsRead，
 * 供首頁、templates、components、pricing 等 landing 頁面使用。
 */
export function useEnterHomeNotificationBanner() {
    const api = useEnterApi();
    const locale = useLocale();
    const t = useTranslations('notificationDefaults');
    const { login } = useEnterHomeLogin();
    const handleBannerCtaClick = useCallback(
        (banner: NotificationBannerProps) => {
            const link = banner.ctaLink;
            if (!link) return;
            if (banner.ctaLinkType === 'externalLink') {
                window.open(link, '_blank');
            } else {
                login({
                    appState: {
                        returnTo: link,
                    },
                });
            }
        },
        [login]
    );

    const noopMarkAsRead = useCallback(async () => ({ code: 0, message: 'ok', data: null }), []);

    const getPublicActiveBannerMessages = useCallback(async () => {
        const response = await api.notifications.getPublicActiveBannerMessages({ locale });
        return resolveEnterHomeNotificationBannerResponse(response);
    }, [api, locale]);

    const { banner: notificationBanner, onClose: onCloseBanner } = useNotificationBanner(
        getPublicActiveBannerMessages,
        noopMarkAsRead,
        {
            onCtaClick: handleBannerCtaClick,
            displayPage: 'landing',
            requestKey: locale,
            identityKey: 'landing',
            defaultCtaText: t('readMore'),
            locale,
        }
    );

    const handleBannerClose = useCallback(
        (activeBanner?: NotificationBannerCarouselItem) => {
            const bannerId = resolveEnterHomeNotificationBannerCloseId(
                notificationBanner,
                activeBanner
            );
            if (bannerId !== null) onCloseBanner(bannerId);
        },
        [notificationBanner, onCloseBanner]
    );

    return { notificationBanner, handleBannerClose };
}
